import { getSession } from '../session.js';
import { showToast } from './Toast.js';
import { previewAddOrder } from './KitchenDisplay.js';
import { getPreviewTableStore } from './RestaurantTables.js';
import type { MenuItem, Order, RestaurantTable } from '../../core/app-store.js';

type NavigateFn = (route: string, params?: Record<string, string>) => void;
interface CartItem { item: MenuItem; qty: number; notes: string; }
interface TabItem  { name: string; price: number; qty: number; emoji: string; }
interface Tab      { tableName: string; guestName: string; openedAt: string; items: TabItem[]; }

let cart: CartItem[] = [];
let tableNumber = '';
let allMenuItems: MenuItem[] = [];

// ── Tab store (shared window key with RestaurantTables) ───────────────────────
function tabStore(): Record<string, Tab> {
  const w = window as unknown as { __preuve_tabs?: Record<string, Tab> };
  if (!w.__preuve_tabs) w.__preuve_tabs = {};
  return w.__preuve_tabs;
}
function getTab(name: string): Tab | null { return tabStore()[name] ?? null; }
function addToTab(name: string, items: CartItem[]): void {
  const tab = getTab(name);
  if (!tab) return;
  items.forEach((c) => {
    const ex = tab.items.find((i) => i.name === c.item.name);
    if (ex) ex.qty += c.qty;
    else tab.items.push({ name: c.item.name, price: c.item.price, qty: c.qty, emoji: c.item.emoji ?? '🍽' });
  });
}
function closeTab(name: string): void { delete tabStore()[name]; }
function tabTotal(tab: Tab): number { return tab.items.reduce((s, i) => s + i.price * i.qty, 0); }
function cartAmount(): number { return cart.reduce((s, c) => s + c.item.price * c.qty, 0); }
function fullTotal(): number {
  const tab = getTab(tableNumber);
  return (tab ? tabTotal(tab) : 0) + cartAmount();
}

// ── Table options builder ─────────────────────────────────────────────────────

const STATUS_ICON: Record<string, string> = {
  available:       '✓',
  occupied:        '●',
  reserved:        '◆',
  'needs-cleaning':'⚠',
};
const STATUS_LABEL: Record<string, string> = {
  available:       'Open',
  occupied:        'Occupied',
  reserved:        'Reserved',
  'needs-cleaning':'Needs Cleaning',
};

function buildTableOptions(businessId: string, preset: string): string {
  let tables: RestaurantTable[] = [];
  try {
    tables = getPreviewTableStore(businessId);
  } catch { /* no tables */ }

  if (tables.length === 0) {
    return `<option value="${escHtml(preset || '1')}">${escHtml(preset || 'Table 1')}</option>`;
  }

  // Group by section
  const sections = new Map<string, RestaurantTable[]>();
  tables.forEach((t) => {
    const s = t.section || 'Main Floor';
    if (!sections.has(s)) sections.set(s, []);
    sections.get(s)!.push(t);
  });

  const presetName = preset || tables[0]!.name;
  let html = '';
  sections.forEach((tbls, section) => {
    html += `<optgroup label="${escHtml(section)}" style="background:#1e2030;color:#94a3b8;font-weight:700">`;
    tbls.forEach((t) => {
      const icon  = STATUS_ICON[t.status]  ?? '?';
      const label = STATUS_LABEL[t.status] ?? t.status;
      const optLabel = `${t.name}  ${icon} ${label} · ${t.seats} seats`;
      html += `<option value="${escHtml(t.name)}" style="background:#1e2030;color:#e2e8f0" ${t.name === presetName ? 'selected' : ''}>${escHtml(optLabel)}</option>`;
    });
    html += `</optgroup>`;
  });
  return html;
}

// ── Main render ───────────────────────────────────────────────────────────────

export async function renderRestaurantTablet(
  container: HTMLElement,
  navigate: NavigateFn,
  params: Record<string, string> = {},
): Promise<void> {
  const session = getSession();
  cart = [];
  const preset = params['tablePreset'] ?? '';

  container.innerHTML = `
    <div class="tablet-wrap">
      <div class="tablet-topbar">
        <div style="display:flex;align-items:center;gap:12px">
          <button class="btn btn-ghost btn-sm" id="back-btn" style="color:#fff;border-color:rgba(255,255,255,.3)">← Back</button>
          <span style="font-weight:700;font-size:16px">🍽 ${escHtml(session?.businessName ?? 'Restaurant')}</span>
        </div>
        <div style="display:flex;align-items:center;gap:12px">
          <div style="display:flex;align-items:center;gap:8px">
            <label style="color:rgba(255,255,255,.7);font-size:12px;margin-bottom:0">Table</label>
            <select id="table-num"
              style="font-size:13px;font-weight:700;padding:6px 10px;background:rgba(255,255,255,.15);border:1px solid rgba(255,255,255,.3);color:#fff;border-radius:6px;cursor:pointer;min-width:140px">
              ${buildTableOptions(session?.businessId ?? 'preview', preset)}
            </select>
          </div>
          <button id="manage-tables-btn" title="Manage tables" style="padding:6px 8px;border-radius:6px;border:1px solid rgba(255,255,255,.3);background:rgba(255,255,255,.1);color:#fff;cursor:pointer;font-size:14px;line-height:1">⚙</button>
          <button class="btn btn-ghost btn-sm" id="ai-help-btn" style="color:#fff;border-color:rgba(255,255,255,.3);background:rgba(245,158,11,.2)">✨ AI Help</button>
          <button class="btn btn-ghost btn-sm" id="kitchen-btn" style="color:#fff;border-color:rgba(255,255,255,.3)">🍳 Kitchen</button>
        </div>
      </div>

      <div class="tablet-body">
        <div class="tablet-menu-panel" id="menu-panel">
          <div class="tablet-cat-bar" id="cat-bar"></div>
          <div class="tablet-items" id="menu-items"><div class="spinner" style="margin:40px auto;display:block"></div></div>
        </div>

        <!-- Cart + Tab panel -->
        <div class="tablet-cart-panel" style="display:flex;flex-direction:column;overflow:hidden">
          <div style="padding:14px 16px;border-bottom:1px solid var(--border)">
            <div style="font-size:15px;font-weight:700">Current Round</div>
            <div style="font-size:12px;color:var(--text-muted)" id="cart-table-label">${escHtml(preset || '')}</div>
          </div>

          <!-- Tab history (previous rounds) -->
          <div id="tab-history" style="border-bottom:1px solid var(--border);display:none;background:var(--bg-tertiary)"></div>

          <!-- Current cart -->
          <div id="cart-items" style="flex:1;overflow-y:auto;padding:12px"></div>

          <div id="cart-footer" style="padding:14px 16px;border-top:1px solid var(--border)">
            <div id="tab-totals" style="margin-bottom:10px"></div>
            <button class="btn btn-gold btn-lg" style="width:100%;margin-bottom:8px" id="send-order-btn" disabled>
              <i class="bi bi-send"></i> Send to Kitchen
            </button>
            <button id="close-tab-btn" style="width:100%;padding:12px;border-radius:8px;border:2px solid var(--brand-gold);background:transparent;color:var(--brand-gold);font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px" disabled>
              <i class="bi bi-receipt"></i> <span id="close-tab-label">Close Tab &amp; Pay</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Manage Tables Modal -->
    <div id="mgmt-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:2400;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:24px;width:460px;max-height:88vh;display:flex;flex-direction:column">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:16px">
          <div style="font-size:16px;font-weight:800">Manage Tables</div>
          <button id="close-mgmt-modal" style="background:none;border:none;font-size:20px;cursor:pointer;color:var(--text-muted)">✕</button>
        </div>
        <div id="mgmt-list" style="flex:1;overflow-y:auto;margin-bottom:16px"></div>
        <button class="btn btn-gold" id="mgmt-add-section-btn" style="color:#fff;width:100%">
          <i class="bi bi-plus-lg" style="color:#fff"></i> Add New Section
        </button>
      </div>
    </div>

    <!-- Edit Table Mini-Modal -->
    <div id="mgmt-edit-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.8);z-index:2600;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:14px;padding:24px;width:380px">
        <div style="font-size:15px;font-weight:800;margin-bottom:16px" id="mgmt-edit-title">Edit Table</div>
        <div class="form-row-2">
          <div><label>Table Name *</label><input type="text" id="mgmt-edit-name" /></div>
          <div><label>Seats</label><input type="number" id="mgmt-edit-seats" min="1" max="30" value="4" /></div>
        </div>
        <div class="form-row">
          <label>Section</label>
          <select id="mgmt-edit-section"></select>
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:4px">
          <button class="btn btn-ghost" id="mgmt-edit-cancel">Cancel</button>
          <button class="btn btn-gold" id="mgmt-edit-save" style="color:#fff">Save</button>
        </div>
      </div>
    </div>

    <!-- Add Section Mini-Modal -->
    <div id="mgmt-section-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.8);z-index:2600;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:14px;padding:24px;width:360px">
        <div style="font-size:15px;font-weight:800;margin-bottom:16px">New Section</div>
        <div class="form-row">
          <label>Section Name *</label>
          <input type="text" id="mgmt-section-name" placeholder="e.g. Rooftop, Private Dining…" />
        </div>
        <div style="display:flex;gap:8px;justify-content:flex-end;margin-top:4px">
          <button class="btn btn-ghost" id="mgmt-section-cancel">Cancel</button>
          <button class="btn btn-gold" id="mgmt-section-save" style="color:#fff">Create Section</button>
        </div>
      </div>
    </div>

    <!-- AI Help Modal -->
    <div id="ai-help-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.72);z-index:2500;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:24px;width:500px;max-height:90vh;overflow-y:auto">
        <div style="display:flex;align-items:center;gap:10px;margin-bottom:14px">
          <div style="font-size:24px">✨</div>
          <div>
            <div style="font-size:16px;font-weight:800">AI Order Assistant</div>
            <div style="font-size:11px;color:var(--text-muted)">Describe what the guest wants and I'll recommend items.</div>
          </div>
          <button id="close-ai-help" style="margin-left:auto;background:none;border:none;font-size:20px;cursor:pointer;color:var(--text-muted)">✕</button>
        </div>

        <div style="display:flex;gap:6px;flex-wrap:wrap;margin-bottom:12px" id="ai-chips">
          ${['🥗 Vegetarian','🌾 Gluten-free','🪶 Light meal','💪 Hearty meal','🍺 Pair with beer','🍷 Pair with wine','🤝 Something to share','⚡ Quick &amp; simple','🌶 Spicy','🍰 Dessert'].map((c) =>
            `<button class="ai-chip" data-chip="${c}" style="padding:5px 11px;border-radius:20px;border:1px solid var(--border);background:var(--bg-secondary);color:var(--text-secondary);font-size:12px;cursor:pointer">${c}</button>`
          ).join('')}
        </div>

        <div style="display:flex;gap:8px;margin-bottom:14px">
          <input type="text" id="ai-prompt" placeholder="e.g. Something light and vegetarian, or a burger with fries…"
            style="flex:1;padding:10px 14px;border-radius:8px;border:1px solid var(--border);background:var(--bg-input);color:var(--text-primary);font-size:13px" />
          <button class="btn btn-gold" id="ai-go-btn" style="color:#fff;white-space:nowrap">✨ Suggest</button>
        </div>

        <div id="ai-help-thinking" style="display:none;text-align:center;padding:20px 0">
          <div class="spinner" style="margin:0 auto 10px;width:30px;height:30px;border-width:3px"></div>
          <div style="font-size:12px;color:var(--text-muted)">Scanning the menu…</div>
        </div>

        <div id="ai-help-results" style="display:none">
          <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
            <div style="font-size:12px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted)">Recommendations</div>
            <button id="ai-full-meal-btn" style="font-size:11px;padding:4px 10px;border-radius:12px;border:1px solid var(--brand-gold);background:transparent;color:var(--brand-gold);cursor:pointer">
              ✨ Build Complete Meal
            </button>
          </div>
          <div id="ai-help-list" style="display:flex;flex-direction:column;gap:10px"></div>
        </div>
      </div>
    </div>

    <!-- Payment Modal -->
    <div id="payment-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.78);z-index:2000;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:1px solid var(--border);border-radius:16px;padding:28px;width:420px;max-height:90vh;overflow-y:auto">
        <div style="font-size:20px;font-weight:800;margin-bottom:4px">Tab Checkout</div>
        <div style="font-size:12px;color:var(--text-muted);margin-bottom:16px" id="pay-table-label"></div>

        <!-- Itemized tab -->
        <div id="pay-itemized" style="background:var(--bg-secondary);border-radius:8px;padding:12px;margin-bottom:16px;font-size:13px"></div>

        <div style="display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-top:1px solid var(--border);margin-bottom:4px">
          <span style="font-size:13px;color:var(--text-muted)">Subtotal</span>
          <span id="pay-subtotal" style="font-weight:600"></span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;padding:4px 0;margin-bottom:12px">
          <span style="font-size:13px;color:var(--text-muted)">Tax (8.8%)</span>
          <span id="pay-tax" style="font-weight:600"></span>
        </div>
        <div style="display:flex;justify-content:space-between;align-items:center;padding:10px 0;border-top:2px solid var(--border);margin-bottom:20px">
          <span style="font-size:18px;font-weight:800">Total</span>
          <span id="pay-amount" style="font-size:28px;font-weight:900;color:var(--brand-gold)"></span>
        </div>

        <div id="pay-methods" style="display:flex;flex-direction:column;gap:8px">
          <button id="tap-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--brand-gold);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">📱</span> Tap to Pay (NFC)
          </button>
          <button id="card-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--border);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">💳</span> Card Insert / Swipe
          </button>
          <button id="cash-btn" style="display:flex;align-items:center;justify-content:center;gap:10px;padding:14px;border-radius:12px;border:2px solid var(--border);background:transparent;color:var(--text-primary);font-size:14px;font-weight:700;cursor:pointer">
            <span style="font-size:22px">💵</span> Cash
          </button>
        </div>

        <div id="pay-processing" style="display:none;padding:20px 0;text-align:center">
          <div class="spinner" style="margin:0 auto 12px;width:36px;height:36px;border-width:4px"></div>
          <div style="font-size:14px;font-weight:600" id="pay-processing-msg">Processing…</div>
        </div>

        <button id="cancel-pay-btn" style="margin-top:16px;display:block;width:100%;background:none;border:none;color:var(--text-muted);font-size:13px;cursor:pointer;text-decoration:underline">Cancel</button>
      </div>
    </div>

    <!-- Success Modal -->
    <div id="success-modal" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,.85);z-index:3000;align-items:center;justify-content:center">
      <div style="background:var(--bg-card);border:2px solid #10b981;border-radius:20px;padding:48px 40px;width:360px;text-align:center">
        <div style="width:80px;height:80px;border-radius:50%;background:#10b98122;border:3px solid #10b981;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:40px">✓</div>
        <div style="font-size:28px;font-weight:900;color:#10b981;margin-bottom:6px">Payment Complete!</div>
        <div style="font-size:44px;font-weight:900;color:var(--text-primary);margin-bottom:8px" id="success-amount"></div>
        <div style="font-size:13px;color:var(--text-muted);margin-bottom:28px" id="success-table-label"></div>
        <button id="success-done-btn" class="btn btn-gold btn-lg" style="width:100%">Done</button>
      </div>
    </div>
  `;

  // Set initial table from preset or first available option
  const tableSelect = document.getElementById('table-num') as HTMLSelectElement;
  tableNumber = tableSelect.value || preset || '';

  document.getElementById('back-btn')!.addEventListener('click', () => navigate('tables'));
  document.getElementById('kitchen-btn')!.addEventListener('click', () => navigate('kitchen'));
  document.getElementById('manage-tables-btn')!.addEventListener('click', () => openMgmtModal(session?.businessId ?? 'preview'));
  document.getElementById('close-mgmt-modal')!.addEventListener('click', () => { document.getElementById('mgmt-modal')!.style.display = 'none'; });
  document.getElementById('mgmt-add-section-btn')!.addEventListener('click', () => openMgmtSectionModal(session?.businessId ?? 'preview'));
  document.getElementById('mgmt-edit-cancel')!.addEventListener('click', () => { document.getElementById('mgmt-edit-modal')!.style.display = 'none'; });
  document.getElementById('mgmt-section-cancel')!.addEventListener('click', () => { document.getElementById('mgmt-section-modal')!.style.display = 'none'; });
  document.getElementById('ai-help-btn')!.addEventListener('click', openAiHelpModal);
  document.getElementById('close-ai-help')!.addEventListener('click', () => {
    document.getElementById('ai-help-modal')!.style.display = 'none';
  });
  document.getElementById('ai-go-btn')!.addEventListener('click', runAiHelp);
  document.getElementById('ai-prompt')!.addEventListener('keydown', (e) => {
    if ((e as KeyboardEvent).key === 'Enter') runAiHelp();
  });
  document.getElementById('ai-full-meal-btn')!.addEventListener('click', buildCompleteMeal);
  tableSelect.addEventListener('change', () => {
    tableNumber = tableSelect.value;
    document.getElementById('cart-table-label')!.textContent = tableNumber;
    renderCart();
  });

  document.getElementById('send-order-btn')!.addEventListener('click', () => sendOrder(session?.businessId ?? 'preview'));
  document.getElementById('close-tab-btn')!.addEventListener('click', () => openPaymentModal());

  document.getElementById('tap-btn')!.addEventListener('click', () => processPayment('tap'));
  document.getElementById('card-btn')!.addEventListener('click', () => processPayment('card'));
  document.getElementById('cash-btn')!.addEventListener('click', () => processPayment('cash'));
  document.getElementById('cancel-pay-btn')!.addEventListener('click', () => {
    document.getElementById('payment-modal')!.style.display = 'none';
  });
  document.getElementById('success-done-btn')!.addEventListener('click', () => {
    closeTab(tableNumber);
    document.getElementById('success-modal')!.style.display = 'none';
    cart = [];
    renderCart();
    navigate('tables');
  });

  await loadMenuForTablet(session?.businessId ?? 'preview');
  renderCart();
}

// ── Menu loading ──────────────────────────────────────────────────────────────

async function loadMenuForTablet(businessId: string): Promise<void> {
  let items: MenuItem[] = [];
  try {
    items = (await getAPI().getMenuItems(businessId) as MenuItem[]).filter((i) => i.available);
    if (items.length === 0) throw new Error('empty');
  } catch {
    items = getSampleMenu(businessId);
  }
  allMenuItems = items;

  const categories = [...new Set(items.map((i) => i.category))].sort();
  let activeCategory = categories[0] ?? '';

  const catBar = document.getElementById('cat-bar')!;
  catBar.innerHTML = categories.map((c) => `
    <button class="tablet-cat-btn ${c === activeCategory ? 'active' : ''}" data-cat="${escHtml(c)}">${escHtml(c)}</button>
  `).join('');

  catBar.querySelectorAll('.tablet-cat-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      catBar.querySelectorAll('.tablet-cat-btn').forEach((b) => b.classList.remove('active'));
      btn.classList.add('active');
      activeCategory = btn.getAttribute('data-cat')!;
      renderMenuItems(items.filter((i) => i.category === activeCategory));
    });
  });

  renderMenuItems(items.filter((i) => i.category === activeCategory));
}

function renderMenuItems(items: MenuItem[]): void {
  const panel = document.getElementById('menu-items')!;
  panel.innerHTML = items.map((item) => `
    <div class="tablet-menu-item" data-id="${item.id}">
      <div style="font-size:36px;margin-bottom:8px">${item.emoji ?? '🍽'}</div>
      <div style="font-weight:700;font-size:14px;margin-bottom:4px;text-align:center">${escHtml(item.name)}</div>
      ${item.description ? `<div style="font-size:11px;color:var(--text-muted);text-align:center;margin-bottom:8px;line-height:1.4">${escHtml(item.description)}</div>` : ''}
      <div style="font-size:18px;font-weight:800;color:var(--brand-gold)">$${item.price.toFixed(2)}</div>
      <button class="btn btn-gold btn-sm" style="margin-top:10px;width:100%" data-add="${item.id}" data-item='${JSON.stringify(item).replace(/'/g, "&#39;")}'>+ Add</button>
    </div>
  `).join('');

  panel.querySelectorAll('[data-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = JSON.parse(btn.getAttribute('data-item')!.replace(/&#39;/g, "'")) as MenuItem;
      const existing = cart.find((c) => c.item.id === item.id);
      if (existing) existing.qty++; else cart.push({ item, qty: 1, notes: '' });
      renderCart();
    });
  });
}

// ── Cart rendering ────────────────────────────────────────────────────────────

function renderCart(): void {
  const tab = getTab(tableNumber);

  // Tab history section
  const histEl = document.getElementById('tab-history')!;
  if (tab && tab.items.length > 0) {
    histEl.style.display = 'block';
    histEl.innerHTML = `
      <div style="padding:10px 16px;border-bottom:1px solid var(--border)">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.5px;color:var(--text-muted);margin-bottom:8px">
          📋 Tab — ${escHtml(tab.guestName || tab.tableName)}
          <span style="float:right;color:var(--brand-gold)">$${tabTotal(tab).toFixed(2)}</span>
        </div>
        ${tab.items.map((i) => `
          <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--text-secondary);padding:2px 0">
            <span>${i.emoji} ${i.qty}× ${escHtml(i.name)}</span>
            <span>$${(i.price * i.qty).toFixed(2)}</span>
          </div>`).join('')}
      </div>`;
  } else {
    histEl.style.display = 'none';
  }

  // Current cart
  const el = document.getElementById('cart-items')!;
  const cartAmt = cartAmount();
  const hasCart = cart.length > 0;
  const hasTab = tab && tab.items.length > 0;
  const total = fullTotal();

  (document.getElementById('send-order-btn') as HTMLButtonElement).disabled = !hasCart;
  (document.getElementById('close-tab-btn') as HTMLButtonElement).disabled = !hasCart && !hasTab;

  const tax = total * 0.088;
  const withTax = total + tax;
  document.getElementById('close-tab-label')!.textContent =
    hasTab ? `Close Tab & Pay  $${withTax.toFixed(2)}` : `Pay  $${(cartAmt * 1.088).toFixed(2)}`;

  // Totals row
  const totalsEl = document.getElementById('tab-totals')!;
  if (hasTab) {
    totalsEl.innerHTML = `
      <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--text-muted);margin-bottom:4px">
        <span>This round</span><span>$${cartAmt.toFixed(2)}</span>
      </div>
      <div style="display:flex;justify-content:space-between;font-size:14px;font-weight:700;color:var(--brand-gold)">
        <span>Tab Total (before tax)</span><span>$${total.toFixed(2)}</span>
      </div>`;
  } else {
    totalsEl.innerHTML = `
      <div style="display:flex;justify-content:space-between;font-size:16px;font-weight:700">
        <span>Total</span><span style="color:var(--brand-gold)">$${cartAmt.toFixed(2)}</span>
      </div>`;
  }

  if (!hasCart) {
    el.innerHTML = `<div style="text-align:center;color:var(--text-muted);padding:24px;font-size:13px">${hasTab ? 'Add another round →' : 'Tap items from the menu →'}</div>`;
    return;
  }

  el.innerHTML = cart.map((c, i) => `
    <div style="display:flex;align-items:center;gap:8px;padding:9px 0;border-bottom:1px solid var(--border)">
      <div style="font-size:20px">${c.item.emoji ?? '🍽'}</div>
      <div style="flex:1;min-width:0">
        <div style="font-size:13px;font-weight:600">${escHtml(c.item.name)}</div>
        <div style="font-size:11px;color:var(--text-muted)">$${(c.item.price * c.qty).toFixed(2)}</div>
      </div>
      <div style="display:flex;align-items:center;gap:5px">
        <button class="qty-btn" data-action="dec" data-idx="${i}">−</button>
        <span style="font-size:14px;font-weight:700;min-width:20px;text-align:center">${c.qty}</span>
        <button class="qty-btn" data-action="inc" data-idx="${i}">+</button>
        <button class="qty-btn" data-action="del" data-idx="${i}" style="color:var(--danger)">✕</button>
      </div>
    </div>
  `).join('');

  el.querySelectorAll('.qty-btn').forEach((btn) => {
    btn.addEventListener('click', () => {
      const action = btn.getAttribute('data-action')!;
      const idx = parseInt(btn.getAttribute('data-idx')!);
      if (action === 'inc') cart[idx]!.qty++;
      else if (action === 'dec') { cart[idx]!.qty--; if (cart[idx]!.qty <= 0) cart.splice(idx, 1); }
      else cart.splice(idx, 1);
      renderCart();
    });
  });
}

// ── Send to kitchen ───────────────────────────────────────────────────────────

async function sendOrder(businessId: string): Promise<void> {
  const btn = document.getElementById('send-order-btn') as HTMLButtonElement;
  btn.disabled = true;
  btn.innerHTML = '<span class="spinner"></span> Sending…';

  const orderItems = cart.map((c) => ({
    menuItemId: c.item.id, name: c.item.name, quantity: c.qty, price: c.item.price,
  }));

  const newOrder: Order = {
    id: `o_${Date.now()}`, businessId,
    tableNumber: tableNumber || '1',
    items: orderItems, status: 'pending',
    total: cartAmount(),
    createdAt: new Date().toISOString(), updatedAt: new Date().toISOString(),
  };

  try { await getAPI().createOrder({ ...newOrder }); } catch { previewAddOrder(newOrder); }

  // Add current cart to the running tab
  addToTab(tableNumber, cart);

  window.dispatchEvent(new CustomEvent('kitchen:new-order', { detail: { tableNumber } }));
  showToast(`Round sent to kitchen — ${tableNumber}`, 'success');

  cart = [];
  btn.innerHTML = '<i class="bi bi-send"></i> Send to Kitchen';
  renderCart();
}

// ── Payment flow ──────────────────────────────────────────────────────────────

function openPaymentModal(): void {
  // If there are unsent cart items, merge them into the tab for billing
  if (cart.length > 0) addToTab(tableNumber, cart);

  const tab = getTab(tableNumber);
  const subtotal = tab ? tabTotal(tab) : cartAmount();
  const tax = subtotal * 0.088;
  const total = subtotal + tax;

  document.getElementById('pay-table-label')!.textContent = tab
    ? `${tab.tableName}  ·  ${tab.guestName || 'Guest'}  ·  Opened ${new Date(tab.openedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
    : `Table ${tableNumber}`;

  const items = tab ? tab.items : cart.map((c) => ({ name: c.item.name, price: c.item.price, qty: c.qty, emoji: c.item.emoji ?? '🍽' }));
  document.getElementById('pay-itemized')!.innerHTML = items.map((i) => `
    <div style="display:flex;justify-content:space-between;padding:4px 0;border-bottom:1px solid var(--border)40">
      <span>${i.emoji} ${i.qty}× ${escHtml(i.name)}</span>
      <span style="font-weight:600">$${(i.price * i.qty).toFixed(2)}</span>
    </div>`).join('') || '<div style="color:var(--text-muted);font-size:12px">No items</div>';

  document.getElementById('pay-subtotal')!.textContent = `$${subtotal.toFixed(2)}`;
  document.getElementById('pay-tax')!.textContent = `$${tax.toFixed(2)}`;
  document.getElementById('pay-amount')!.textContent = `$${total.toFixed(2)}`;

  document.getElementById('pay-methods')!.style.display = 'flex';
  document.getElementById('pay-processing')!.style.display = 'none';
  (document.getElementById('cancel-pay-btn') as HTMLButtonElement).style.display = 'block';
  document.getElementById('payment-modal')!.style.display = 'flex';
}

function processPayment(method: 'tap' | 'card' | 'cash'): void {
  const msgs: Record<string, string> = {
    tap: '📱 Hold card or phone near reader…',
    card: '💳 Insert or swipe card…',
    cash: '💵 Processing cash payment…',
  };
  document.getElementById('pay-methods')!.style.display = 'none';
  (document.getElementById('cancel-pay-btn') as HTMLButtonElement).style.display = 'none';
  document.getElementById('pay-processing')!.style.display = 'block';
  document.getElementById('pay-processing-msg')!.textContent = msgs[method] ?? 'Processing…';

  setTimeout(() => {
    document.getElementById('payment-modal')!.style.display = 'none';
    const total = parseFloat(document.getElementById('pay-amount')!.textContent?.replace('$', '') ?? '0');
    document.getElementById('success-amount')!.textContent = `$${total.toFixed(2)}`;
    document.getElementById('success-table-label')!.textContent = `Table ${tableNumber} · Payment Complete`;
    document.getElementById('success-modal')!.style.display = 'flex';
  }, method === 'cash' ? 700 : 1800);
}

// ── Sample menu with alcoholic drinks ─────────────────────────────────────────

function getSampleMenu(businessId: string): MenuItem[] {
  return [
    // Food
    { id: 'f1', businessId, name: 'Classic Burger',    description: 'Angus beef, cheddar, lettuce',     price: 14.99, category: 'Burgers',     available: true, emoji: '🍔' },
    { id: 'f2', businessId, name: 'BBQ Bacon Burger',  description: 'Crispy bacon, BBQ sauce',          price: 17.99, category: 'Burgers',     available: true, emoji: '🍔' },
    { id: 'f3', businessId, name: 'Caesar Salad',      description: 'Romaine, parmesan, croutons',      price: 11.99, category: 'Salads',      available: true, emoji: '🥗' },
    { id: 'f4', businessId, name: 'Grilled Salmon',    description: 'Lemon butter, asparagus',          price: 24.99, category: 'Entrées',     available: true, emoji: '🍣' },
    { id: 'f5', businessId, name: 'Chicken Alfredo',   description: 'House-made alfredo, fettuccine',   price: 18.99, category: 'Entrées',     available: true, emoji: '🍝' },
    { id: 'f6', businessId, name: 'Mozzarella Sticks', description: 'Fried mozz, marinara',             price:  8.99, category: 'Appetizers',  available: true, emoji: '🧀' },
    { id: 'f7', businessId, name: 'Lava Cake',         description: 'Dark chocolate, vanilla ice cream',price:  8.99, category: 'Desserts',    available: true, emoji: '🍫' },
    // Non-alcoholic
    { id: 'n1', businessId, name: 'Craft Lemonade',    description: 'Fresh squeezed, seasonal',         price:  4.99, category: 'Drinks',      available: true, emoji: '🍋' },
    { id: 'n2', businessId, name: 'Sparkling Water',   description: 'San Pellegrino',                   price:  3.99, category: 'Drinks',      available: true, emoji: '💧' },
    { id: 'n3', businessId, name: 'Iced Coffee',       description: 'Cold brew, milk',                  price:  4.99, category: 'Drinks',      available: true, emoji: '☕' },
    // Beer & Cider
    { id: 'b1', businessId, name: 'Draft Lager',       description: 'Light & crisp house lager',        price:  5.99, category: 'Beer & Cider', available: true, emoji: '🍺' },
    { id: 'b2', businessId, name: 'IPA Draft',         description: 'Pacific Northwest IPA, 6.5% ABV',  price:  6.99, category: 'Beer & Cider', available: true, emoji: '🍺' },
    { id: 'b3', businessId, name: 'Stout',             description: 'Dark roasted, creamy finish',      price:  6.99, category: 'Beer & Cider', available: true, emoji: '🍺' },
    { id: 'b4', businessId, name: 'Hard Cider',        description: 'Local apple cider, 5% ABV',        price:  6.49, category: 'Beer & Cider', available: true, emoji: '🍎' },
    // Wine
    { id: 'w1', businessId, name: 'House Red',         description: 'Cabernet blend, glass',            price:  8.99, category: 'Wine',         available: true, emoji: '🍷' },
    { id: 'w2', businessId, name: 'House White',       description: 'Chardonnay, glass',                price:  8.99, category: 'Wine',         available: true, emoji: '🥂' },
    { id: 'w3', businessId, name: 'Rosé',              description: 'Dry rosé, glass',                  price:  9.99, category: 'Wine',         available: true, emoji: '🌸' },
    { id: 'w4', businessId, name: 'Prosecco',          description: 'By the glass',                     price: 10.99, category: 'Wine',         available: true, emoji: '🍾' },
    // Cocktails
    { id: 'c1', businessId, name: 'Margarita',         description: 'Tequila, lime, triple sec, salt',  price: 11.99, category: 'Cocktails',    available: true, emoji: '🍹' },
    { id: 'c2', businessId, name: 'Mojito',            description: 'Rum, mint, lime, soda',            price: 11.99, category: 'Cocktails',    available: true, emoji: '🍸' },
    { id: 'c3', businessId, name: 'Old Fashioned',     description: 'Bourbon, bitters, orange',         price: 12.99, category: 'Cocktails',    available: true, emoji: '🥃' },
    { id: 'c4', businessId, name: 'Cosmopolitan',      description: 'Vodka, cranberry, triple sec',     price: 11.99, category: 'Cocktails',    available: true, emoji: '🍸' },
    { id: 'c5', businessId, name: 'Moscow Mule',       description: 'Vodka, ginger beer, lime',         price: 11.99, category: 'Cocktails',    available: true, emoji: '🥄' },
    // Spirits
    { id: 's1', businessId, name: 'Well Whiskey',      description: 'Neat or on the rocks',             price:  7.99, category: 'Spirits',      available: true, emoji: '🥃' },
    { id: 's2', businessId, name: 'Well Vodka',        description: 'Neat, rocks, or soda',             price:  7.99, category: 'Spirits',      available: true, emoji: '🍸' },
    { id: 's3', businessId, name: 'Tequila Shot',      description: 'With lime and salt',               price:  7.99, category: 'Spirits',      available: true, emoji: '🥃' },
    { id: 's4', businessId, name: 'Bourbon Shot',      description: 'Premium bourbon, 1.5oz',           price:  8.99, category: 'Spirits',      available: true, emoji: '🥃' },
  ];
}

// ── Table Management (in-tablet) ─────────────────────────────────────────────

function refreshTableSelect(businessId: string): void {
  const sel = document.getElementById('table-num') as HTMLSelectElement | null;
  if (!sel) return;
  const current = sel.value;
  sel.innerHTML = buildTableOptions(businessId, current);
  // Restore selection
  if (Array.from(sel.options).some((o) => o.value === current)) sel.value = current;
}

function renderMgmtList(businessId: string): void {
  const tables = getPreviewTableStore(businessId);
  const sections = new Map<string, RestaurantTable[]>();
  tables.forEach((t) => {
    const s = t.section || 'Main Floor';
    if (!sections.has(s)) sections.set(s, []);
    sections.get(s)!.push(t);
  });

  const list = document.getElementById('mgmt-list')!;
  list.innerHTML = [...sections.entries()].map(([section, tbls]) => `
    <div style="margin-bottom:18px">
      <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
        <div style="font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.8px;color:var(--text-muted)">${escHtml(section)}</div>
        <div style="flex:1;height:1px;background:var(--border)"></div>
        <button data-add-to-section="${escHtml(section)}"
          style="font-size:11px;padding:3px 9px;border-radius:10px;border:1px solid var(--brand-gold);background:transparent;color:var(--brand-gold);cursor:pointer">
          + Table
        </button>
      </div>
      ${tbls.map((t) => {
        const icon  = STATUS_ICON[t.status]  ?? '?';
        const label = STATUS_LABEL[t.status] ?? t.status;
        return `
        <div style="display:flex;align-items:center;gap:10px;padding:8px 10px;border:1px solid var(--border);border-radius:8px;margin-bottom:6px;background:var(--bg-secondary)">
          <div style="flex:1;min-width:0">
            <div style="font-size:13px;font-weight:700">${escHtml(t.name)}</div>
            <div style="font-size:11px;color:var(--text-muted)">${icon} ${label} · ${t.seats} seats · ${escHtml(section)}</div>
          </div>
          <button data-mgmt-edit="${escHtml(t.id)}"
            style="padding:4px 9px;border-radius:6px;border:1px solid var(--border);background:transparent;color:var(--text-secondary);font-size:11px;cursor:pointer">
            ✏ Edit
          </button>
          <button data-mgmt-delete="${escHtml(t.id)}"
            style="padding:4px 9px;border-radius:6px;border:1px solid #ef444466;background:transparent;color:#ef4444;font-size:11px;cursor:pointer">
            ✕
          </button>
        </div>`;
      }).join('')}
    </div>
  `).join('');

  // Wire edit buttons
  list.querySelectorAll('[data-mgmt-edit]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset['mgmtEdit']!;
      const t = getPreviewTableStore(businessId).find((x) => x.id === id);
      if (t) openMgmtEditModal(t, businessId);
    });
  });

  // Wire delete buttons
  list.querySelectorAll('[data-mgmt-delete]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = (btn as HTMLElement).dataset['mgmtDelete']!;
      if (!confirm('Remove this table?')) return;
      const store = window as unknown as { __preuve_tables?: RestaurantTable[] };
      if (store.__preuve_tables) store.__preuve_tables = store.__preuve_tables.filter((t) => t.id !== id);
      renderMgmtList(businessId);
      refreshTableSelect(businessId);
    });
  });

  // Wire "+ Table" buttons
  list.querySelectorAll('[data-add-to-section]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const section = (btn as HTMLElement).dataset['addToSection']!;
      const tables = getPreviewTableStore(businessId);
      const sectionTables = tables.filter((t) => (t.section || 'Main Floor') === section);
      const prefix = section === 'Main Floor' ? 'Table' : section;
      let max = 0;
      sectionTables.forEach((t) => { const m = t.name.match(/(\d+)$/); if (m) max = Math.max(max, parseInt(m[1]!)); });
      const newName = `${prefix} ${max + 1}`;
      const store = window as unknown as { __preuve_tables?: RestaurantTable[] };
      store.__preuve_tables!.push({ id: `t${Date.now()}`, businessId, name: newName, seats: 4, status: 'available', section });
      renderMgmtList(businessId);
      refreshTableSelect(businessId);
    });
  });
}

function openMgmtModal(businessId: string): void {
  renderMgmtList(businessId);
  document.getElementById('mgmt-modal')!.style.display = 'flex';
}

let mgmtEditingId: string | null = null;

function openMgmtEditModal(table: RestaurantTable, businessId: string): void {
  mgmtEditingId = table.id;
  document.getElementById('mgmt-edit-title')!.textContent = `Edit — ${table.name}`;
  (document.getElementById('mgmt-edit-name') as HTMLInputElement).value = table.name;
  (document.getElementById('mgmt-edit-seats') as HTMLInputElement).value = String(table.seats);

  const sections = Array.from(new Set(getPreviewTableStore(businessId).map((t) => t.section || 'Main Floor')));
  const sel = document.getElementById('mgmt-edit-section') as HTMLSelectElement;
  sel.innerHTML = sections.map((s) => `<option value="${escHtml(s)}" ${s === (table.section || 'Main Floor') ? 'selected' : ''}>${escHtml(s)}</option>`).join('');

  // Wire save (replace listener)
  const saveBtn = document.getElementById('mgmt-edit-save')!;
  const newSave = saveBtn.cloneNode(true) as HTMLElement;
  saveBtn.replaceWith(newSave);
  newSave.addEventListener('click', () => {
    const name = (document.getElementById('mgmt-edit-name') as HTMLInputElement).value.trim();
    const seats = parseInt((document.getElementById('mgmt-edit-seats') as HTMLInputElement).value) || 4;
    const section = (document.getElementById('mgmt-edit-section') as HTMLSelectElement).value;
    if (!name) return;
    const store = window as unknown as { __preuve_tables?: RestaurantTable[] };
    const t = store.__preuve_tables?.find((x) => x.id === mgmtEditingId);
    if (t) { t.name = name; t.seats = seats; t.section = section; }
    document.getElementById('mgmt-edit-modal')!.style.display = 'none';
    renderMgmtList(businessId);
    refreshTableSelect(businessId);
  });

  document.getElementById('mgmt-edit-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('mgmt-edit-name') as HTMLInputElement).focus(), 50);
}

function openMgmtSectionModal(businessId: string): void {
  (document.getElementById('mgmt-section-name') as HTMLInputElement).value = '';

  const saveBtn = document.getElementById('mgmt-section-save')!;
  const newSave = saveBtn.cloneNode(true) as HTMLElement;
  saveBtn.replaceWith(newSave);
  newSave.addEventListener('click', () => {
    const name = (document.getElementById('mgmt-section-name') as HTMLInputElement).value.trim();
    if (!name) return;
    const store = window as unknown as { __preuve_tables?: RestaurantTable[] };
    if (!store.__preuve_tables) getPreviewTableStore(businessId);
    store.__preuve_tables!.push({ id: `t${Date.now()}`, businessId, name: `${name} 1`, seats: 4, status: 'available', section: name });
    document.getElementById('mgmt-section-modal')!.style.display = 'none';
    renderMgmtList(businessId);
    refreshTableSelect(businessId);
  });

  document.getElementById('mgmt-section-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('mgmt-section-name') as HTMLInputElement).focus(), 50);
}

// ── AI Help ───────────────────────────────────────────────────────────────────

function openAiHelpModal(): void {
  (document.getElementById('ai-prompt') as HTMLInputElement).value = '';
  document.getElementById('ai-help-thinking')!.style.display = 'none';
  document.getElementById('ai-help-results')!.style.display = 'none';

  // Wire quick-pick chips
  document.querySelectorAll('.ai-chip').forEach((chip) => {
    const c = chip as HTMLElement;
    c.style.background = 'var(--bg-secondary)';
    c.style.color = 'var(--text-secondary)';
    c.style.borderColor = 'var(--border)';
    c.onclick = () => {
      const raw = c.dataset['chip']!.replace(/&amp;/g, '&');
      const prompt = document.getElementById('ai-prompt') as HTMLInputElement;
      prompt.value = raw.replace(/^[\p{Emoji}\s]+/u, '').trim();
      // Highlight selected chip
      document.querySelectorAll('.ai-chip').forEach((x) => {
        (x as HTMLElement).style.background = 'var(--bg-secondary)';
        (x as HTMLElement).style.color = 'var(--text-secondary)';
        (x as HTMLElement).style.borderColor = 'var(--border)';
      });
      c.style.background = 'var(--brand-gold)22';
      c.style.color = 'var(--brand-gold)';
      c.style.borderColor = 'var(--brand-gold)';
      runAiHelp();
    };
  });

  document.getElementById('ai-help-modal')!.style.display = 'flex';
  setTimeout(() => (document.getElementById('ai-prompt') as HTMLInputElement).focus(), 50);
}

interface AiMenuRec { item: MenuItem; score: number; reason: string; }

function scoreMenuItems(query: string): AiMenuRec[] {
  const q = query.toLowerCase();
  const keywords = {
    vegetarian: ['vegetarian', 'vegan', 'veggie', 'plant', 'no meat'],
    light:      ['light', 'small', 'quick', 'simple', 'low cal', 'healthy', 'salad'],
    hearty:     ['hearty', 'filling', 'hungry', 'big', 'heavy', 'large'],
    seafood:    ['seafood', 'fish', 'salmon', 'shrimp', 'ocean'],
    spicy:      ['spicy', 'hot', 'jalapeño', 'heat'],
    sweet:      ['sweet', 'dessert', 'chocolate'],
    beer:       ['beer', 'ale', 'lager', 'ipa', 'stout', 'cider', 'brew'],
    wine:       ['wine', 'red wine', 'white wine', 'rosé', 'prosecco', 'bubbly'],
    cocktail:   ['cocktail', 'mixed drink', 'martini', 'margarita', 'mojito'],
    share:      ['share', 'sharing', 'appetizer', 'starter', 'starters'],
    drink:      ['drink', 'beverage', 'thirsty', 'something to drink'],
    gluten:     ['gluten', 'gluten-free', 'gf', 'celiac'],
    pairing:    ['pair', 'pairing', 'goes with', 'match', 'complement'],
  };

  // Determine which categories match
  const wantsVegetarian = keywords.vegetarian.some((k) => q.includes(k));
  const wantsLight      = keywords.light.some((k) => q.includes(k));
  const wantsHearty     = keywords.hearty.some((k) => q.includes(k));
  const wantsSeafood    = keywords.seafood.some((k) => q.includes(k));
  const wantsSpicy      = keywords.spicy.some((k) => q.includes(k));
  const wantsSweet      = keywords.sweet.some((k) => q.includes(k));
  const wantsBeer       = keywords.beer.some((k) => q.includes(k));
  const wantsWine       = keywords.wine.some((k) => q.includes(k));
  const wantsCocktail   = keywords.cocktail.some((k) => q.includes(k));
  const wantsShare      = keywords.share.some((k) => q.includes(k));
  const wantsDrink      = keywords.drink.some((k) => q.includes(k));
  const wantsPairing    = keywords.pairing.some((k) => q.includes(k));

  // Cart contents for pairing logic
  const cartCategories = new Set(cart.map((c) => c.item.category));
  const hasFood = [...cartCategories].some((c) => !['Beer & Cider','Wine','Cocktails','Spirits','Drinks'].includes(c));
  const hasDrinks = [...cartCategories].some((c) => ['Beer & Cider','Wine','Cocktails','Spirits','Drinks'].includes(c));

  const MEAT_ITEMS = ['burger', 'beef', 'chicken', 'bacon', 'steak', 'salmon', 'lobster', 'wing'];
  const DRINK_CATS = ['Beer & Cider', 'Wine', 'Cocktails', 'Spirits', 'Drinks'];

  return allMenuItems.map((item): AiMenuRec => {
    const name = item.name.toLowerCase();
    const desc = (item.description ?? '').toLowerCase();
    const cat  = item.category;
    const isDrink = DRINK_CATS.includes(cat);
    const hasMeat = MEAT_ITEMS.some((m) => name.includes(m) || desc.includes(m));
    let score = 0;
    let reason = '';

    if (wantsVegetarian) {
      if (!hasMeat && !isDrink) { score += 40; reason = 'Vegetarian-friendly'; }
      else if (hasMeat) score -= 30;
    }
    if (wantsLight && (cat === 'Salads' || cat === 'Appetizers')) {
      score += 35; reason = reason || 'Light option';
    }
    if (wantsHearty && (cat === 'Burgers' || cat === 'Entrées')) {
      score += 35; reason = reason || 'Hearty and filling';
    }
    if (wantsSeafood && (name.includes('salmon') || name.includes('shrimp') || name.includes('fish'))) {
      score += 40; reason = reason || 'Fresh seafood';
    }
    if (wantsSpicy && (desc.includes('spic') || desc.includes('jalap') || desc.includes('hot'))) {
      score += 35; reason = reason || 'Has some heat';
    }
    if (wantsSweet && (cat === 'Desserts' || desc.includes('chocolate') || desc.includes('sweet'))) {
      score += 40; reason = reason || 'Sweet treat';
    }
    if ((wantsBeer || wantsDrink) && cat === 'Beer & Cider') { score += 40; reason = reason || 'Cold draft beer'; }
    if ((wantsWine || wantsDrink) && cat === 'Wine') { score += 40; reason = reason || 'Great wine selection'; }
    if ((wantsCocktail || wantsDrink) && cat === 'Cocktails') { score += 40; reason = reason || 'House cocktail'; }
    if (wantsShare && cat === 'Appetizers') { score += 30; reason = reason || 'Great for sharing'; }

    // Pairing: if guest has food but no drink, suggest drinks; vice versa
    if (wantsPairing) {
      if (hasFood && isDrink) { score += 30; reason = reason || 'Pairs well with your food'; }
      if (hasDrinks && !isDrink) { score += 25; reason = reason || 'Complements your drinks'; }
    }

    // Generic keyword match against name/description
    const words = q.split(/\s+/).filter((w) => w.length > 3);
    words.forEach((w) => {
      if (name.includes(w) || desc.includes(w)) { score += 15; reason = reason || `Matches "${item.name}"`; }
    });

    // Slight boost for popular categories when no strong signal
    if (score === 0 && !isDrink) score = 5 + Math.random() * 5;
    if (!reason) reason = `Popular ${cat.toLowerCase()} choice`;

    return { item, score, reason };
  })
  .filter((r) => r.score > 0)
  .sort((a, b) => b.score - a.score);
}

function renderAiResults(recs: AiMenuRec[]): void {
  const top = recs.slice(0, 5);
  document.getElementById('ai-help-list')!.innerHTML = top.map((rec) => `
    <div style="display:flex;align-items:center;gap:12px;padding:12px;border:1px solid var(--border);border-radius:10px;background:var(--bg-secondary)">
      <div style="font-size:30px;flex-shrink:0">${rec.item.emoji ?? '🍽'}</div>
      <div style="flex:1;min-width:0">
        <div style="font-size:14px;font-weight:700">${escHtml(rec.item.name)}</div>
        <div style="font-size:11px;color:var(--brand-gold);margin-bottom:2px">${escHtml(rec.reason)}</div>
        ${rec.item.description ? `<div style="font-size:11px;color:var(--text-muted)">${escHtml(rec.item.description)}</div>` : ''}
      </div>
      <div style="text-align:right;flex-shrink:0">
        <div style="font-size:15px;font-weight:800;color:var(--brand-gold);margin-bottom:6px">$${rec.item.price.toFixed(2)}</div>
        <button data-ai-add='${JSON.stringify(rec.item).replace(/'/g, "&#39;")}' data-ai-add-name="${escHtml(rec.item.name)}"
          class="btn btn-gold btn-sm" style="color:#fff;font-size:11px">+ Add</button>
      </div>
    </div>
  `).join('');

  document.querySelectorAll('[data-ai-add]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const item = JSON.parse(btn.getAttribute('data-ai-add')!.replace(/&#39;/g, "'")) as MenuItem;
      const existing = cart.find((c) => c.item.id === item.id);
      if (existing) existing.qty++; else cart.push({ item, qty: 1, notes: '' });
      renderCart();
      const name = (btn as HTMLElement).dataset['aiAddName']!;
      (btn as HTMLElement).textContent = '✓ Added';
      (btn as HTMLElement).style.background = '#10b981';
      setTimeout(() => { (btn as HTMLElement).textContent = '+ Add'; (btn as HTMLElement).style.background = ''; }, 1500);
    });
  });

  document.getElementById('ai-help-results')!.style.display = 'block';
}

function runAiHelp(): void {
  const query = (document.getElementById('ai-prompt') as HTMLInputElement).value.trim();
  if (!query) return;
  document.getElementById('ai-help-thinking')!.style.display = 'block';
  document.getElementById('ai-help-results')!.style.display = 'none';
  setTimeout(() => {
    document.getElementById('ai-help-thinking')!.style.display = 'none';
    const recs = scoreMenuItems(query);
    if (recs.length === 0) {
      document.getElementById('ai-help-list')!.innerHTML =
        '<div style="color:var(--text-muted);font-size:13px;text-align:center;padding:16px">No strong matches — try a different description.</div>';
      document.getElementById('ai-help-results')!.style.display = 'block';
    } else {
      renderAiResults(recs);
    }
  }, 700);
}

function buildCompleteMeal(): void {
  const FOOD_CATS  = ['Appetizers', 'Salads', 'Burgers', 'Entrées'];
  const DRINK_CATS = ['Beer & Cider', 'Wine', 'Cocktails', 'Drinks'];

  const pick = (cats: string[]) => {
    const pool = allMenuItems.filter((i) => cats.includes(i.category));
    return pool[Math.floor(Math.random() * pool.length)];
  };

  const starter = pick(['Appetizers', 'Salads']);
  const main    = pick(['Burgers', 'Entrées']);
  const drink   = pick(DRINK_CATS);

  const meal = [starter, main, drink].filter(Boolean) as MenuItem[];
  meal.forEach((item) => {
    const existing = cart.find((c) => c.item.id === item.id);
    if (existing) existing.qty++; else cart.push({ item, qty: 1, notes: '' });
  });
  renderCart();
  document.getElementById('ai-help-modal')!.style.display = 'none';
}

function getAPI() {
  return (window as unknown as { preuve: Record<string, (...args: unknown[]) => Promise<unknown>> }).preuve;
}
function escHtml(str: string): string {
  return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
